import { Select } from "antd";
import "antd/dist/reset.css"; // reset styles for AntD + Tailwind
import { useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { useDashboardGraphDataQuery } from "../../../../features/superAdmin/dashboard/dashboardApi";
import { DFilters } from "../Dashboard";
import WithLoader from "./WithLoader";

const COLORS = {
  complete: "#37EFD8",
  approved: "#76C5FF",
  created: "#FFC692",
};

const ConfiguredTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-md text-sm">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between gap-6">
            <div className="flex items-center gap-2">
              <div
                className={`h-3 w-[3px] rounded-full`}
                style={{ background: entry.fill }}
              />
              <span className="font-medium" style={{ color: entry.fill }}>
                {entry.name}
              </span>
            </div>
            <span className="text-display-body">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const StackBar = (props: any) => {
  const { x, y, width, height, fill } = props;
  const GAP = 4;

  return (
    <rect
      x={x}
      y={y + GAP / 2}
      width={width}
      height={height - GAP}
      fill={fill}
      rx={4}
      ry={4}
    />
  );
};

export default function TicketPerformance({
  toDate,
  fromDate,
  limit: l,
  skip: p,
  ...filters
}: DFilters) {
  const [period, setPeriod] = useState<"week" | "month" | "6months">("month");

  const { data, isLoading, isFetching } = useDashboardGraphDataQuery({
    params: {
      period,
      ...filters,
      fromDate: fromDate || undefined,
      toDate: toDate || undefined,
    },
  });
  const { raisedTicket, approvedTicket, completedTicket } = data || {};

  const allPeriods = new Set([
    ...(raisedTicket?.map((r) => r.period) || []),
    ...(approvedTicket?.map((a) => a.period) || []),
    ...(completedTicket?.map((c) => c.period) || []),
  ]);

  const combined = Array.from(allPeriods).map((period) => {
    const raised =
      raisedTicket?.find((r) => r.period === period)?.total_records ||
      raisedTicket?.find((r) => r.period === period)?.tickets_raised ||
      0;

    const approved =
      approvedTicket?.find((a) => a.period === period)?.total_records ||
      approvedTicket?.find((a) => a.period === period)?.tickets_approved ||
      0;

    const complete =
      completedTicket?.find((c) => c.period === period)?.total_records ||
      completedTicket?.find((c) => c.period === period)?.tickets_completed ||
      0;

    return {
      date: period,
      created: raised,
      approved,
      complete,
    };
  });

  return (
    <div className="shadow p-4 rounded-lg space-y-5">
      <div className="flex items-center justify-between">
        <h4 className="text-xl text-header font-medium">Ticket Performance</h4>
        <Select
          className="h-10"
          placeholder="This Month"
          options={[
            { label: "This Week", value: "week" },
            { label: "This Month", value: "month" },
            { label: "Last 6 Months", value: "6months" },
          ]}
          onSelect={(val) => setPeriod(val)}
        />
      </div>
      <WithLoader isLoading={isLoading || isFetching}>
        <div className="w-full h-[358px]">
          <ResponsiveContainer>
            <BarChart data={combined} barSize={36}>
              <CartesianGrid
                vertical={false}
                strokeDasharray="3 3"
                stroke="#D7DFE9"
              />
              <XAxis dataKey="date" />
              {/* <YAxis /> */}
              <Tooltip
                cursor={{ fill: "transparent" }}
                content={<ConfiguredTooltip />}
              />
              <Legend />
              <Bar
                dataKey="complete"
                stackId="a"
                fill={COLORS.complete}
                name="Ticket Closed"
                radius={[4, 4, 4, 4]}
                shape={<StackBar />}
              />
              <Bar
                dataKey="approved"
                stackId="a"
                fill={COLORS.approved}
                name="Ticket Resolved"
                radius={[4, 4, 4, 4]}
                shape={<StackBar />}
              />
              <Bar
                dataKey="created"
                stackId="a"
                fill={COLORS.created}
                name="Created Tickets"
                radius={[4, 4, 4, 4]}
                shape={<StackBar />}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </WithLoader>
    </div>
  );
}
